import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '../ThemeContext';

const GLITCH_CHARS = '!@#$%^&*()_+-=[]{}|;:,.<>?/~`0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

function getRandomChar() {
  return GLITCH_CHARS[Math.floor(Math.random() * GLITCH_CHARS.length)];
}

export default function GlitchText({
  text,
  as: Tag = 'span',
  className = '',
  cyberClassName = '',
  cleanClassName = '',
  decodeSpeed = 50,
  decodeDelay = 0,
  jitterInterval = 3000,
  jitterDuration = 150,
}) {
  const { isCyberpunk } = useTheme();
  const [displayText, setDisplayText] = useState(text);
  const [isDecoding, setIsDecoding] = useState(false);
  const prevCyber = useRef(isCyberpunk);
  const jitterTimer = useRef(null);
  const timers = useRef([]);  // all active IDs from runDecode
  const runId = useRef(0);    // cancellation token — increment to invalidate in-flight callbacks

  const clearDecodeTimers = useCallback(() => {
    timers.current.forEach(id => clearTimeout(id));
    timers.current = [];
  }, []);

  const runDecode = useCallback(() => {
    clearDecodeTimers();
    const id = ++runId.current;

    setIsDecoding(true);
    const target = text;
    const len = target.length;
    const cyclesBeforeReveal = 3;
    const staggerGap = 2;

    if (decodeDelay > 0) {
      setDisplayText(Array.from({ length: len }, () => getRandomChar()).join(''));

      const scrambleId = setInterval(() => {
        if (runId.current !== id) { clearInterval(scrambleId); return; }
        setDisplayText(Array.from({ length: len }, () => getRandomChar()).join(''));
      }, decodeSpeed);
      timers.current.push(scrambleId);

      const scrambleClearId = setTimeout(() => clearInterval(scrambleId), decodeDelay);
      timers.current.push(scrambleClearId);
    }

    const startId = setTimeout(() => {
      if (runId.current !== id) return;

      const positionCounters = Array.from({ length: len }, (_, i) => -(i * staggerGap));
      const revealed = new Array(len).fill(false);

      setDisplayText(Array.from({ length: len }, () => getRandomChar()).join(''));

      const intervalId = setInterval(() => {
        if (runId.current !== id) { clearInterval(intervalId); return; }

        const result = [];
        for (let i = 0; i < len; i++) {
          if (revealed[i]) {
            result.push(target[i]);
          } else {
            positionCounters[i]++;
            if (positionCounters[i] >= cyclesBeforeReveal) {
              revealed[i] = true;
              result.push(target[i]);
            } else {
              result.push(getRandomChar());
            }
          }
        }

        setDisplayText(result.join(''));

        if (revealed.every(Boolean)) {
          clearInterval(intervalId);
          if (runId.current === id) {
            setDisplayText(target);
            setIsDecoding(false);
          }
        }
      }, decodeSpeed);

      timers.current.push(intervalId);
    }, decodeDelay);

    timers.current.push(startId);
  }, [text, decodeSpeed, decodeDelay, clearDecodeTimers]);

  // Trigger decode on switch to cyber; immediately reset on switch to clean
  useEffect(() => {
    if (isCyberpunk && !prevCyber.current) {
      runDecode();
    } else if (!isCyberpunk && prevCyber.current) {
      runId.current++;       // invalidates all in-flight callbacks
      clearDecodeTimers();
      setDisplayText(text);
      setIsDecoding(false);
    }
    prevCyber.current = isCyberpunk;
  }, [isCyberpunk, runDecode, text, clearDecodeTimers]);

  // Periodic jitter in cyberpunk mode
  const runJitter = useCallback(() => {
    if (!isCyberpunk || isDecoding) return;
    const target = text;
    const len = target.length;
    const numGlitch = Math.floor(Math.random() * 3) + 1;
    const glitchPositions = new Set();
    for (let i = 0; i < numGlitch; i++) {
      glitchPositions.add(Math.floor(Math.random() * len));
    }
    const glitched = target.split('').map((ch, i) =>
      glitchPositions.has(i) ? getRandomChar() : ch
    ).join('');
    setDisplayText(glitched);
    setTimeout(() => setDisplayText(target), jitterDuration);
  }, [isCyberpunk, isDecoding, text, jitterDuration]);

  useEffect(() => {
    if (jitterTimer.current) clearTimeout(jitterTimer.current);

    if (isCyberpunk && !isDecoding) {
      const scheduleNext = () => {
        const delay = jitterInterval + (Math.random() - 0.5) * jitterInterval * 0.6;
        jitterTimer.current = setTimeout(() => {
          runJitter();
          scheduleNext();
        }, delay);
      };
      scheduleNext();
    }

    return () => {
      if (jitterTimer.current) clearTimeout(jitterTimer.current);
    };
  }, [isCyberpunk, isDecoding, jitterInterval, runJitter]);

  useEffect(() => {
    if (!isDecoding) setDisplayText(text);
  }, [text, isDecoding]);

  const appliedClass = `${className} ${isCyberpunk ? cyberClassName : cleanClassName}`.trim();

  return <Tag className={appliedClass}>{displayText}</Tag>;
}
